// Visual tokens for the Collections tab.
// Keeps the buttons identical to the Surveys tab it replaces (#002824 primary,
// #70DFDA accent) and reuses the shared derin palette for everything else.
import { derinColors } from "../theme";

export const font = "Inter, sans-serif";

export const collectionColors = {
  textPrimary: derinColors.textPrimary,
  textSecondary: derinColors.textSecondary,
  white: derinColors.backgroundWhite,
  surfaceLight: derinColors.backgroundLight,
  border: derinColors.borderLight,
  buttonPrimary: "#002824",
  buttonPrimaryHover: "#003d38",
  buttonAccent: "#70DFDA",
  buttonAccentHover: "#5fd0cb",
  /** The accent at 20%, for the "applied file" pills in a card's footer. */
  chipApplied: "rgba(112, 223, 218, 0.2)",
  danger: "#DC2626",
  dangerSurface: "rgba(220, 38, 38, 0.08)",
  /** Hover of the light-red (dangerSurface) buttons. */
  dangerSurfaceHover: "rgba(220, 38, 38, 0.14)",
  dropzoneActive: "#1976d2",
  dropzoneActiveSurface: "rgba(25, 118, 210, 0.04)",
};

const baseButtonSx = {
  height: "32px",
  padding: "6px 16px 6px 10px",
  gap: "8px",
  borderRadius: "8px",
  fontFamily: font,
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "-0.150391px",
  textTransform: "none" as const,
  whiteSpace: "nowrap" as const,
};

export const primaryButtonSx = {
  ...baseButtonSx,
  background: collectionColors.buttonPrimary,
  color: collectionColors.white,
  "&:hover": { background: collectionColors.buttonPrimaryHover },
  "&.Mui-disabled": { background: collectionColors.buttonPrimary, opacity: 0.5, color: collectionColors.white },
};

export const accentButtonSx = {
  ...baseButtonSx,
  background: collectionColors.buttonAccent,
  border: `1px solid ${collectionColors.border}`,
  color: collectionColors.textPrimary,
  "&:hover": { background: collectionColors.buttonAccentHover },
};

export const outlinedButtonSx = {
  ...baseButtonSx,
  background: collectionColors.white,
  border: `1px solid ${collectionColors.border}`,
  color: collectionColors.textPrimary,
  "&:hover": { background: collectionColors.surfaceLight },
};

export const inputSx = {
  boxSizing: "border-box" as const,
  width: "100%",
  height: "36px",
  padding: "4px 12px",
  background: collectionColors.surfaceLight,
  borderRadius: "8px",
  border: "none",
  outline: "none",
  fontFamily: font,
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "17px",
  letterSpacing: "-0.150391px",
  color: collectionColors.textPrimary,
  "&::placeholder": { color: collectionColors.textSecondary },
};

export const fieldLabelSx = {
  fontFamily: font,
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "14px",
  letterSpacing: "-0.150391px",
  color: collectionColors.textPrimary,
};

export const captionLabelSx = {
  fontFamily: font,
  fontWeight: 500,
  fontSize: "11px",
  lineHeight: "16px",
  letterSpacing: "0.4px",
  textTransform: "uppercase" as const,
  color: collectionColors.textSecondary,
};

export const subtitleSx = {
  fontFamily: font,
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "-0.150391px",
  color: collectionColors.textSecondary,
};

/** Small bordered pill — versions ("v2"), variant counts, status. */
export const chipSx = {
  display: "flex",
  alignItems: "center",
  padding: "2px 8px",
  border: `1px solid ${collectionColors.border}`,
  borderRadius: "8px",
  fontFamily: font,
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "16px",
  color: collectionColors.textPrimary,
  whiteSpace: "nowrap" as const,
};

/** Filled dark pill — "Primary variant", current version marker. */
export const solidChipSx = {
  ...chipSx,
  background: collectionColors.textPrimary,
  border: "1px solid transparent",
  color: collectionColors.white,
};

/** Pale teal pill — a file that is applied to the collection, in the card footer. */
export const appliedChipSx = {
  ...chipSx,
  gap: "6px",
  maxWidth: "260px",
  background: collectionColors.chipApplied,
  border: "1px solid transparent",
};

export const formatDate = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleDateString() : "—";

export const formatDateTime = (value: string | null | undefined): string =>
  value
    ? `${new Date(value).toLocaleDateString()} at ${new Date(value).toLocaleTimeString()}`
    : "—";

export const isYamlFile = (file: File): boolean => /\.(ya?ml)$/i.test(file.name);

/**
 * Report deliverable formats, matching the API's allowlist. Kept as extensions rather
 * than MIME types for the same reason the API does: browsers disagree about the Office
 * ones, and several send `application/octet-stream`.
 */
export const REPORT_EXTENSIONS = [".pdf", ".ppt", ".pptx", ".doc", ".docx", ".xls", ".xlsx"];

export const REPORT_ACCEPT = REPORT_EXTENSIONS.join(",");

export const isReportFile = (file: File): boolean =>
  REPORT_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension));

/** "4.2 MB". Reports run to tens of megabytes, so KB alone would not read well. */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** "Q3 Readout.pptx" → "Q3 Readout" — the default title, matching the API's. */
export const fileNameStem = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf(".");
  return (lastDot > 0 ? fileName.slice(0, lastDot) : fileName).trim();
};
